import Button from "components/button";
import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { Link } from "react-router-dom";
import { PlusIcon } from '@heroicons/react/solid'
import { Filter, OrgCard, OrgTable } from "./components";
import { useTable, useUpdate } from "@refinedev/core";
import { IOrgItem } from "interfaces";
import { useMemo } from "react";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { isHaveActionPermission } from "utills/helpers";

export const OrganizationList: React.FC = () => {
  const {
    tableQueryResult,
    filters,
    setFilters,
  } = useTable<IOrgItem>({
    resource: "organization",
    syncWithLocation: true,
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "organizations",
    },
  });

  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("org_management", "organization", "edit"))
      return;
    if (window.confirm(`Are you sure you want to ${isActive ? "deactivate" : "activate"}  this Organization?`)) {
      updateMutation({
        resource: "organization",
        id,
        meta: {
          URLSuffix: `active/${!isActive}`,
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : [],
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      gte: logicalFilters.find((item) => item.field === "gte")?.value || "",
      lte: logicalFilters.find((item) => item.field === "lte")?.value || "",
      use_whitelabel_integration: logicalFilters.find((item) => item.field === "use_whitelabel_integration")?.value || "",
      page: logicalFilters.find((item) => item.field === "page")?.value || "1",
    };
  }, [filters]);

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }

  if (tableQueryResult.isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission(
          "org_management",
          "organization",
          "create"
        ) && {
          additionalLabel: (
            <Link to={"/organization/org/create"} target="_blank">
              <Button>
                <span className="sr-only">Create New Organization</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Create New Organization
                </span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter currentFilterValues={currentFilterValues} setFilters={setFilters} />
      </div>
      <div className="box-container">
        {!tableQueryResult.data?.data?.length &&
        !tableQueryResult.isFetching && (
          <div className="text-center">
            No data
          </div>
        )}

        {tableQueryResult?.data?.data && tableQueryResult?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <OrgCard 
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <OrgTable 
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="mt-6">
              <Pagination 
                currentPage={tableQueryResult?.data?.paging.current_page}
                totalPage={tableQueryResult?.data?.paging.total_page}
                total={{
                  totalItems: tableQueryResult?.data?.paging.total_item || 0,
                  total: tableQueryResult?.data?.paging.total
                }}
                onChange={_handlePageChanged}
              />
            </div>
          </>
        ) : <></>}
      </div>
      {(tableQueryResult.isFetching && tableQueryResult.isPreviousData) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
};